import { C, type UnitDef } from './types';

const MIL = C.Military;

/**
 * Unit roster. Statistics are initial balance values; see docs/BALANCE.md.
 * Counter relationships emerge from armour classes and bonus damage:
 *  spear > cavalry, archer > infantry, cavalry > archer/siege, heavy infantry > light infantry,
 *  siege > buildings, gunpowder > armoured, artillery > formations/buildings.
 */
export const UNITS: UnitDef[] = [
  {
    key: 'villager', name: 'Villager', classes: C.Villager, cost: [50, 0, 0, 0], trainTime: 20,
    hp: 25, armor: { melee: 0, pierce: 0 },
    attack: { melee: 4, pierce: 0, range: 0, cooldown: 2, bonuses: [{ vs: C.Building, dmg: 3 }] },
    speed: 0.9, los: 4, pop: 1, age: 0, trainedAt: 'townCenter', radius: 0.22, model: 'villager', weapon: 'axe',
    gatherRates: [0.45, 0.48, 0.45, 0.42], carryCapacity: 10,
    description: 'Gathers resources, constructs and repairs buildings.',
  },
  {
    key: 'militia', name: 'Militia', classes: C.Infantry | MIL, cost: [60, 0, 20, 0], trainTime: 21,
    hp: 40, armor: { melee: 0, pierce: 1 },
    attack: { melee: 4, pierce: 0, range: 0, cooldown: 2, bonuses: [{ vs: C.Building, dmg: 2 }] },
    speed: 0.9, los: 4, pop: 1, age: 0, trainedAt: 'barracks', radius: 0.24, model: 'infantry', weapon: 'sword', helmet: 'cap', shield: true,
    description: 'Cheap infantry. Strong against buildings and other light infantry.',
  },
  {
    key: 'spearman', name: 'Spearman', classes: C.Infantry | C.Spear | MIL, cost: [35, 25, 0, 0], trainTime: 22,
    hp: 45, armor: { melee: 0, pierce: 0 },
    attack: { melee: 3, pierce: 0, range: 0, cooldown: 3, bonuses: [{ vs: C.Cavalry, dmg: 15 }, { vs: C.Elephant, dmg: 15 }] },
    speed: 1.0, los: 4, pop: 1, age: 0, trainedAt: 'barracks', radius: 0.24, model: 'spear', weapon: 'spear', helmet: 'cap',
    description: 'Anti-cavalry infantry. Weak against archers.',
  },
  {
    key: 'scout', name: 'Scout Rider', classes: C.Cavalry | C.Scout | MIL, cost: [80, 0, 0, 0], trainTime: 30,
    hp: 45, armor: { melee: 0, pierce: 2 },
    attack: { melee: 3, pierce: 0, range: 0, cooldown: 2, bonuses: [{ vs: C.Villager, dmg: 2 }] },
    speed: 1.55, los: 7, pop: 1, age: 0, trainedAt: 'barracks', radius: 0.38, model: 'scout', weapon: 'sword',
    description: 'Fast reconnaissance cavalry with wide line of sight.',
  },
  {
    key: 'archer', name: 'Archer', classes: C.Archer | MIL, cost: [25, 45, 0, 0], trainTime: 30,
    hp: 30, armor: { melee: 0, pierce: 0 },
    attack: { melee: 0, pierce: 4, range: 5, cooldown: 2, projectile: 'arrow', projectileSpeed: 9, accuracy: 85, bonuses: [{ vs: C.Spear, dmg: 3 }, { vs: C.Infantry, dmg: 1 }] },
    speed: 0.96, los: 6, pop: 1, age: 1, trainedAt: 'archeryRange', radius: 0.22, model: 'archer', weapon: 'bow', helmet: 'cap',
    description: 'Ranged infantry. Strong against spearmen and slow infantry, weak against cavalry.',
  },
  {
    key: 'crossbowman', name: 'Crossbowman', classes: C.Archer | MIL, cost: [25, 45, 0, 0], trainTime: 27,
    hp: 35, armor: { melee: 0, pierce: 0 },
    attack: { melee: 0, pierce: 5, range: 5, cooldown: 2, projectile: 'bolt', projectileSpeed: 11, accuracy: 85, bonuses: [{ vs: C.Spear, dmg: 3 }, { vs: C.Heavy, dmg: 2 }] },
    speed: 0.96, los: 7, pop: 1, age: 1, trainedAt: 'archeryRange', radius: 0.22, model: 'crossbow', weapon: 'crossbow', helmet: 'kettle',
    description: 'Armour-piercing ranged infantry.',
  },
  {
    key: 'lightCavalry', name: 'Light Cavalry', classes: C.Cavalry | MIL, cost: [80, 0, 0, 0], trainTime: 30,
    hp: 60, armor: { melee: 0, pierce: 2 },
    attack: { melee: 7, pierce: 0, range: 0, cooldown: 2, bonuses: [{ vs: C.Archer, dmg: 4 }, { vs: C.Siege, dmg: 6 }] },
    speed: 1.5, los: 8, pop: 1, age: 1, trainedAt: 'stable', radius: 0.38, model: 'cavalry', weapon: 'sword',
    description: 'Mobile raider. Strong against archers and siege, weak against spearmen.',
  },
  {
    key: 'swordsman', name: 'Swordsman', classes: C.Infantry | MIL, cost: [60, 0, 20, 0], trainTime: 21,
    hp: 60, armor: { melee: 1, pierce: 1 },
    attack: { melee: 9, pierce: 0, range: 0, cooldown: 2, bonuses: [{ vs: C.Spear, dmg: 3 }, { vs: C.Building, dmg: 3 }] },
    speed: 0.9, los: 4, pop: 1, age: 1, trainedAt: 'barracks', radius: 0.25, model: 'infantry', weapon: 'sword', helmet: 'kettle', shield: true,
    description: 'Heavy infantry. Strong against light infantry and buildings.',
  },
  {
    key: 'pikeman', name: 'Pikeman', classes: C.Infantry | C.Spear | MIL, cost: [35, 25, 0, 0], trainTime: 22,
    hp: 55, armor: { melee: 0, pierce: 0 },
    attack: { melee: 4, pierce: 0, range: 0, cooldown: 3, bonuses: [{ vs: C.Cavalry, dmg: 22 }, { vs: C.Elephant, dmg: 25 }] },
    speed: 1.0, los: 4, pop: 1, age: 2, trainedAt: 'barracks', radius: 0.25, model: 'spear', weapon: 'spear', helmet: 'kettle',
    description: 'Improved anti-cavalry infantry.',
  },
  {
    key: 'knight', name: 'Knight', classes: C.Cavalry | C.Heavy | MIL, cost: [60, 0, 75, 0], trainTime: 30,
    hp: 100, armor: { melee: 2, pierce: 2 },
    attack: { melee: 10, pierce: 0, range: 0, cooldown: 1.8, bonuses: [{ vs: C.Archer, dmg: 2 }] },
    speed: 1.35, los: 4, pop: 1, age: 2, trainedAt: 'stable', radius: 0.4, model: 'heavyCavalry', weapon: 'sword', helmet: 'great',
    description: 'Heavy shock cavalry. Excellent all-round, countered by pikemen.',
  },
  {
    key: 'horseArcher', name: 'Cavalry Archer', classes: C.Cavalry | C.MountedArcher | C.Archer | MIL, cost: [0, 40, 70, 0], trainTime: 34,
    hp: 50, armor: { melee: 0, pierce: 0 },
    attack: { melee: 0, pierce: 6, range: 4, cooldown: 2, projectile: 'arrow', projectileSpeed: 9, accuracy: 80, bonuses: [{ vs: C.Spear, dmg: 2 }] },
    speed: 1.4, los: 5, pop: 1, age: 2, trainedAt: 'archeryRange', radius: 0.38, model: 'horseArcher', weapon: 'bow',
    description: 'Mobile ranged cavalry. Strong against slow infantry, weak against massed archers.',
  },
  {
    key: 'ram', name: 'Battering Ram', classes: C.Siege | MIL, cost: [0, 160, 75, 0], trainTime: 36,
    hp: 175, armor: { melee: 0, pierce: 180 },
    attack: { melee: 2, pierce: 0, range: 0, cooldown: 5, bonuses: [{ vs: C.Building, dmg: 125 }] },
    speed: 0.5, los: 3, pop: 3, age: 2, trainedAt: 'siegeWorkshop', radius: 0.55, model: 'ram',
    description: 'Destroys buildings. Immune to arrows, vulnerable to melee.',
  },
  {
    key: 'mangonel', name: 'Mangonel', classes: C.Siege | MIL, cost: [0, 160, 135, 0], trainTime: 46,
    hp: 50, armor: { melee: 0, pierce: 6 },
    attack: { melee: 40, pierce: 0, range: 7, minRange: 3, cooldown: 6, projectile: 'stone', projectileSpeed: 6, splash: 1.0, accuracy: 70, bonuses: [{ vs: C.Building, dmg: 35 }] },
    speed: 0.6, los: 9, pop: 3, age: 2, trainedAt: 'siegeWorkshop', radius: 0.55, model: 'mangonel',
    description: 'Area-damage siege. Devastating against massed units.',
  },
  {
    key: 'handCannoneer', name: 'Hand Cannoneer', classes: C.Gunpowder | C.Archer | MIL, cost: [45, 0, 50, 0], trainTime: 34,
    hp: 40, armor: { melee: 1, pierce: 0 },
    attack: { melee: 0, pierce: 17, range: 6, cooldown: 3.5, projectile: 'bullet', projectileSpeed: 20, accuracy: 75, bonuses: [{ vs: C.Infantry, dmg: 10 }, { vs: C.Heavy, dmg: 6 }] },
    speed: 0.96, los: 8, pop: 1, age: 3, trainedAt: 'archeryRange', radius: 0.24, model: 'gunner', weapon: 'musket', helmet: 'kettle',
    description: 'Early gunpowder infantry. Shreds armoured units.',
  },
  {
    key: 'cuirassier', name: 'Cuirassier', classes: C.Cavalry | C.Heavy | MIL, cost: [60, 0, 90, 0], trainTime: 30,
    hp: 140, armor: { melee: 3, pierce: 4 },
    attack: { melee: 14, pierce: 0, range: 0, cooldown: 1.9, bonuses: [{ vs: C.Archer, dmg: 3 }] },
    speed: 1.35, los: 5, pop: 1, age: 3, trainedAt: 'stable', radius: 0.42, model: 'heavyCavalry', weapon: 'sword', helmet: 'great',
    description: 'Elite heavy cavalry.',
  },
  {
    key: 'bombard', name: 'Bombard Cannon', classes: C.Siege | C.Gunpowder | C.Artillery | MIL, cost: [0, 225, 225, 0], trainTime: 56,
    hp: 80, armor: { melee: 2, pierce: 5 },
    attack: { melee: 40, pierce: 0, range: 12, minRange: 1, cooldown: 6.5, projectile: 'ball', projectileSpeed: 14, splash: 0.5, accuracy: 92, bonuses: [{ vs: C.Building, dmg: 200 }, { vs: C.Siege, dmg: 40 }] },
    speed: 0.6, los: 14, pop: 3, age: 3, trainedAt: 'siegeWorkshop', radius: 0.55, model: 'cannon',
    description: 'Long-range gunpowder siege against buildings.',
  },
  {
    key: 'trebuchet', name: 'Trebuchet', classes: C.Siege | MIL, cost: [0, 200, 200, 0], trainTime: 50,
    hp: 150, armor: { melee: 1, pierce: 150 },
    attack: { melee: 200, pierce: 0, range: 16, minRange: 4, cooldown: 10, projectile: 'stone', projectileSpeed: 7, splash: 0.6, accuracy: 80, bonuses: [{ vs: C.Building, dmg: 250 }] },
    speed: 0.5, los: 16, pop: 4, age: 3, trainedAt: 'castle', radius: 0.7, model: 'trebuchet',
    description: 'Extreme-range siege engine for reducing fortifications.',
  },
  {
    key: 'lineInfantry', name: 'Line Infantry', classes: C.Infantry | C.Gunpowder | MIL, cost: [50, 0, 45, 0], trainTime: 28,
    hp: 70, armor: { melee: 1, pierce: 2 },
    attack: { melee: 0, pierce: 18, range: 6, cooldown: 3, projectile: 'bullet', projectileSpeed: 22, accuracy: 85, bonuses: [{ vs: C.Cavalry, dmg: 6 }, { vs: C.Heavy, dmg: 8 }] },
    speed: 1.0, los: 8, pop: 1, age: 4, trainedAt: 'arsenal', radius: 0.25, model: 'gunner', weapon: 'musket', helmet: 'tricorn',
    description: 'Disciplined musket infantry fighting in ranks.',
  },
  {
    key: 'rifleman', name: 'Rifleman', classes: C.Infantry | C.Gunpowder | C.Archer | MIL, cost: [40, 20, 60, 0], trainTime: 30,
    hp: 60, armor: { melee: 0, pierce: 3 },
    attack: { melee: 0, pierce: 22, range: 9, cooldown: 3.2, projectile: 'bullet', projectileSpeed: 26, accuracy: 92, bonuses: [{ vs: C.Infantry, dmg: 6 }] },
    speed: 1.05, los: 10, pop: 1, age: 4, trainedAt: 'arsenal', radius: 0.24, model: 'gunner', weapon: 'musket', helmet: 'cap',
    description: 'Long-range skirmisher. Weak against cavalry charges.',
  },
  {
    key: 'dragoon', name: 'Dragoon', classes: C.Cavalry | C.Gunpowder | MIL, cost: [80, 0, 80, 0], trainTime: 32,
    hp: 120, armor: { melee: 2, pierce: 3 },
    attack: { melee: 12, pierce: 0, range: 0, cooldown: 1.8, bonuses: [{ vs: C.Artillery, dmg: 30 }, { vs: C.Archer, dmg: 6 }] },
    speed: 1.6, los: 7, pop: 1, age: 4, trainedAt: 'stable', radius: 0.4, model: 'cavalry', weapon: 'sword', helmet: 'tricorn',
    description: 'Advanced cavalry that hunts artillery and skirmishers.',
  },
  {
    key: 'fieldArtillery', name: 'Field Artillery', classes: C.Siege | C.Artillery | C.Gunpowder | MIL, cost: [0, 150, 200, 0], trainTime: 45,
    hp: 120, armor: { melee: 2, pierce: 8 },
    attack: { melee: 50, pierce: 0, range: 11, minRange: 2, cooldown: 5, projectile: 'shell', projectileSpeed: 18, splash: 1.3, accuracy: 85, bonuses: [{ vs: C.Infantry, dmg: 20 }, { vs: C.Building, dmg: 60 }] },
    speed: 0.75, los: 12, pop: 3, age: 4, trainedAt: 'foundry', radius: 0.55, model: 'cannon',
    description: 'Mobile artillery that breaks infantry formations.',
  },
  {
    key: 'heavyArtillery', name: 'Heavy Artillery', classes: C.Siege | C.Artillery | C.Gunpowder | C.Heavy | MIL, cost: [0, 250, 300, 0], trainTime: 60,
    hp: 180, armor: { melee: 2, pierce: 10 },
    attack: { melee: 80, pierce: 0, range: 15, minRange: 3, cooldown: 7, projectile: 'shell', projectileSpeed: 18, splash: 1.0, accuracy: 85, bonuses: [{ vs: C.Building, dmg: 300 }, { vs: C.Siege, dmg: 60 }] },
    speed: 0.5, los: 15, pop: 4, age: 4, trainedAt: 'foundry', radius: 0.65, model: 'cannon',
    description: 'Siege artillery that levels fortifications from great range.',
  },

  // ---- Unique units ----
  {
    key: 'longbowman', name: 'Longbowman', civ: 'english', classes: C.Archer | C.Unique | MIL, cost: [35, 40, 0, 0], trainTime: 26,
    hp: 35, armor: { melee: 0, pierce: 1 },
    attack: { melee: 0, pierce: 6, range: 7, cooldown: 2, projectile: 'arrow', projectileSpeed: 10, accuracy: 90, bonuses: [{ vs: C.Spear, dmg: 3 }, { vs: C.Infantry, dmg: 2 }] },
    speed: 0.96, los: 8, pop: 1, age: 2, trainedAt: 'castle', radius: 0.22, model: 'archer', weapon: 'bow', helmet: 'cap',
    description: 'English unique: long-range archer.',
  },
  {
    key: 'royalKnight', name: 'Royal Knight', civ: 'french', classes: C.Cavalry | C.Heavy | C.Unique | MIL, cost: [70, 0, 80, 0], trainTime: 28,
    hp: 130, armor: { melee: 3, pierce: 3 },
    attack: { melee: 12, pierce: 0, range: 0, cooldown: 1.8, bonuses: [{ vs: C.Archer, dmg: 4 }, { vs: C.Infantry, dmg: 2 }] },
    speed: 1.4, los: 5, pop: 1, age: 2, trainedAt: 'castle', radius: 0.42, model: 'heavyCavalry', weapon: 'spear', helmet: 'great',
    description: 'French unique: elite lance cavalry.',
  },
  {
    key: 'landsknecht', name: 'Landsknecht', civ: 'germans', classes: C.Infantry | C.Heavy | C.Unique | MIL, cost: [65, 0, 40, 0], trainTime: 22,
    hp: 75, armor: { melee: 2, pierce: 1 },
    attack: { melee: 12, pierce: 0, range: 0, cooldown: 2.2, splash: 0.6, bonuses: [{ vs: C.Infantry, dmg: 4 }, { vs: C.Cavalry, dmg: 4 }] },
    speed: 0.95, los: 4, pop: 1, age: 2, trainedAt: 'castle', radius: 0.26, model: 'infantry', weapon: 'sword', helmet: 'feather',
    description: 'German unique: two-handed swordsman with sweeping strikes.',
  },
  {
    key: 'conquistador', name: 'Conquistador', civ: 'spanish', classes: C.Cavalry | C.Gunpowder | C.Unique | MIL, cost: [60, 0, 70, 0], trainTime: 26,
    hp: 75, armor: { melee: 2, pierce: 2 },
    attack: { melee: 0, pierce: 16, range: 4, cooldown: 2.8, projectile: 'bullet', projectileSpeed: 20, accuracy: 85, bonuses: [{ vs: C.Infantry, dmg: 4 }] },
    speed: 1.35, los: 7, pop: 1, age: 2, trainedAt: 'castle', radius: 0.4, model: 'horseArcher', weapon: 'musket', helmet: 'kettle',
    description: 'Spanish unique: mounted gunpowder cavalry.',
  },
  {
    key: 'caravel', name: 'Caravel', civ: 'portuguese', classes: C.Ship | C.Unique | MIL, cost: [0, 110, 50, 0], trainTime: 36,
    hp: 160, armor: { melee: 0, pierce: 7 },
    attack: { melee: 0, pierce: 8, range: 7, cooldown: 2.5, projectile: 'arrow', projectileSpeed: 10, accuracy: 90 },
    speed: 1.5, los: 9, pop: 2, age: 2, trainedAt: 'dock', radius: 0.7, model: 'ship',
    description: 'Portuguese unique: fast exploration and escort warship.',
  },
  {
    key: 'janissary', name: 'Janissary', civ: 'ottomans', classes: C.Infantry | C.Gunpowder | C.Unique | MIL, cost: [60, 0, 55, 0], trainTime: 22,
    hp: 50, armor: { melee: 1, pierce: 0 },
    attack: { melee: 0, pierce: 17, range: 8, cooldown: 3.4, projectile: 'bullet', projectileSpeed: 20, accuracy: 80, bonuses: [{ vs: C.Infantry, dmg: 4 }, { vs: C.Heavy, dmg: 6 }] },
    speed: 0.96, los: 9, pop: 1, age: 2, trainedAt: 'castle', radius: 0.24, model: 'gunner', weapon: 'musket', helmet: 'turban',
    description: 'Ottoman unique: elite gunpowder infantry.',
  },
  {
    key: 'samurai', name: 'Samurai', civ: 'japanese', classes: C.Infantry | C.Unique | MIL, cost: [60, 0, 30, 0], trainTime: 9,
    hp: 70, armor: { melee: 1, pierce: 1 },
    attack: { melee: 10, pierce: 0, range: 0, cooldown: 1.4, bonuses: [{ vs: C.Unique, dmg: 10 }, { vs: C.Infantry, dmg: 2 }] },
    speed: 1.0, los: 5, pop: 1, age: 2, trainedAt: 'castle', radius: 0.25, model: 'infantry', weapon: 'sword', helmet: 'kabuto',
    description: 'Japanese unique: fast-attacking elite infantry.',
  },
  {
    key: 'chuKoNu', name: 'Chu Ko Nu', civ: 'chinese', classes: C.Archer | C.Unique | MIL, cost: [40, 35, 0, 0], trainTime: 19,
    hp: 40, armor: { melee: 0, pierce: 0 },
    attack: { melee: 0, pierce: 3, range: 4, cooldown: 0.9, projectile: 'bolt', projectileSpeed: 11, accuracy: 80, bonuses: [{ vs: C.Infantry, dmg: 1 }] },
    speed: 0.96, los: 6, pop: 1, age: 2, trainedAt: 'castle', radius: 0.22, model: 'crossbow', weapon: 'crossbow', helmet: 'cap',
    description: 'Chinese unique: rapid-fire repeating crossbow.',
  },
  {
    key: 'mangudai', name: 'Mangudai', civ: 'mongols', classes: C.Cavalry | C.MountedArcher | C.Archer | C.Unique | MIL, cost: [0, 55, 65, 0], trainTime: 26,
    hp: 60, armor: { melee: 0, pierce: 0 },
    attack: { melee: 0, pierce: 6, range: 4, cooldown: 2.1, projectile: 'arrow', projectileSpeed: 10, accuracy: 85, bonuses: [{ vs: C.Siege, dmg: 3 }, { vs: C.Infantry, dmg: 1 }] },
    speed: 1.5, los: 6, pop: 1, age: 2, trainedAt: 'castle', radius: 0.38, model: 'horseArcher', weapon: 'bow', helmet: 'cap',
    description: 'Mongol unique: swift horse archer.',
  },
  {
    key: 'warElephant', name: 'War Elephant', civ: 'indians', classes: C.Elephant | C.Heavy | C.Unique | MIL, cost: [170, 0, 85, 0], trainTime: 31,
    hp: 450, armor: { melee: 1, pierce: 2 },
    attack: { melee: 18, pierce: 0, range: 0, cooldown: 2.0, splash: 0.6, bonuses: [{ vs: C.Building, dmg: 7 }] },
    speed: 0.75, los: 5, pop: 2, age: 2, trainedAt: 'castle', radius: 0.7, model: 'elephant',
    description: 'Vijayanagara unique: slow, immensely durable war elephant.',
  },
  {
    key: 'jaguarWarrior', name: 'Jaguar Warrior', civ: 'aztecs', classes: C.Infantry | C.Unique | MIL, cost: [60, 0, 30, 0], trainTime: 17,
    hp: 50, armor: { melee: 1, pierce: 1 },
    attack: { melee: 10, pierce: 0, range: 0, cooldown: 2, bonuses: [{ vs: C.Infantry, dmg: 10 }] },
    speed: 1.0, los: 4, pop: 1, age: 2, trainedAt: 'castle', radius: 0.24, model: 'infantry', weapon: 'club', helmet: 'feather', shield: true,
    description: 'Aztec unique: anti-infantry elite warrior.',
  },
  {
    key: 'royalGuard', name: 'Elite Royal Guard', civ: 'egyptians', classes: C.Infantry | C.Heavy | C.Unique | MIL, cost: [55, 0, 40, 0], trainTime: 22,
    hp: 80, armor: { melee: 3, pierce: 2 },
    attack: { melee: 9, pierce: 0, range: 0, cooldown: 2, bonuses: [{ vs: C.Cavalry, dmg: 6 }, { vs: C.Building, dmg: 4 }] },
    speed: 0.9, los: 4, pop: 1, age: 2, trainedAt: 'castle', radius: 0.25, model: 'spear', weapon: 'spear', helmet: 'great', shield: true,
    description: 'Egyptian unique: heavily armoured guard infantry.',
  },
];

/** Naval units (trained at the Dock). */
UNITS.push(
  {
    key: 'fishingShip', name: 'Fishing Ship', classes: C.Ship, cost: [0, 75, 0, 0], trainTime: 30,
    hp: 60, armor: { melee: 0, pierce: 4 }, speed: 1.3, los: 5, pop: 1, age: 0, trainedAt: 'dock', radius: 0.45, model: 'ship', weapon: 'none',
    gatherRates: [0.55, 0, 0, 0], carryCapacity: 15,
    description: 'Harvests fish and returns it to a Dock.',
  },
  {
    key: 'galley', name: 'Galley', classes: C.Ship | MIL, cost: [0, 90, 30, 0], trainTime: 40,
    hp: 120, armor: { melee: 0, pierce: 6 },
    attack: { melee: 0, pierce: 6, range: 6, cooldown: 2.5, projectile: 'arrow', projectileSpeed: 10, accuracy: 85, bonuses: [{ vs: C.Ship, dmg: 4 }, { vs: C.Building, dmg: 2 }] },
    speed: 1.4, los: 8, pop: 2, age: 1, trainedAt: 'dock', radius: 0.6, model: 'ship', weapon: 'bow',
    description: 'Oared warship firing arrows. Controls the water early.',
  },
  {
    key: 'frigate', name: 'Frigate', classes: C.Ship | C.Gunpowder | MIL, cost: [0, 180, 110, 0], trainTime: 50,
    hp: 260, armor: { melee: 0, pierce: 8 },
    attack: { melee: 30, pierce: 0, range: 9, cooldown: 4, projectile: 'ball', projectileSpeed: 14, splash: 0.5, accuracy: 85, bonuses: [{ vs: C.Ship, dmg: 20 }, { vs: C.Building, dmg: 30 }] },
    speed: 1.35, los: 10, pop: 3, age: 3, trainedAt: 'dock', radius: 0.75, model: 'ship', weapon: 'musket',
    description: 'Gunpowder warship that bombards ships and coastal buildings.',
  },
);

/** Aztec-specific alternatives that replace cavalry and European gunpowder lines. */
UNITS.push(
  {
    key: 'eagleScout', name: 'Eagle Scout', civ: 'aztecs', classes: C.Infantry | C.Scout | MIL, cost: [60, 0, 20, 0], trainTime: 28,
    hp: 50, armor: { melee: 0, pierce: 2 },
    attack: { melee: 4, pierce: 0, range: 0, cooldown: 2, bonuses: [{ vs: C.Villager, dmg: 2 }, { vs: C.Archer, dmg: 2 }] },
    speed: 1.3, los: 7, pop: 1, age: 0, trainedAt: 'barracks', radius: 0.24, model: 'infantry', weapon: 'club', helmet: 'feather', shield: true,
    description: 'Aztec fast scouting infantry, replacing the Scout Rider.',
  },
  {
    key: 'eagleWarrior', name: 'Eagle Warrior', civ: 'aztecs', classes: C.Infantry | MIL, cost: [25, 0, 50, 0], trainTime: 20,
    hp: 60, armor: { melee: 0, pierce: 3 },
    attack: { melee: 8, pierce: 0, range: 0, cooldown: 2, bonuses: [{ vs: C.Archer, dmg: 6 }, { vs: C.Siege, dmg: 8 }, { vs: C.Artillery, dmg: 10 }] },
    speed: 1.25, los: 6, pop: 1, age: 1, trainedAt: 'barracks', radius: 0.24, model: 'infantry', weapon: 'club', helmet: 'feather', shield: true,
    description: 'Aztec mobile infantry that hunts archers and siege; fills the light cavalry role.',
  },
  {
    key: 'arrowKnight', name: 'Arrow Knight', civ: 'aztecs', classes: C.Archer | C.Heavy | MIL, cost: [40, 40, 50, 0], trainTime: 28,
    hp: 75, armor: { melee: 1, pierce: 3 },
    attack: { melee: 0, pierce: 12, range: 8, cooldown: 2.6, projectile: 'arrow', projectileSpeed: 11, accuracy: 92, bonuses: [{ vs: C.Infantry, dmg: 4 }, { vs: C.Artillery, dmg: 12 }] },
    speed: 0.96, los: 9, pop: 1, age: 3, trainedAt: 'archeryRange', radius: 0.24, model: 'archer', weapon: 'bow', helmet: 'feather',
    description: 'Aztec late-age elite archer, replacing gunpowder infantry.',
  },
);

/**
 * Units added after release. They are registered after every original building and resource (see
 * registry.ts), so saved games and replays keep their kind numbers.
 */
export const LATER_UNITS: UnitDef[] = [
  {
    key: 'monk', name: 'Monk', classes: C.Healer, cost: [0, 0, 100, 0], trainTime: 50,
    hp: 30, armor: { melee: 0, pierce: 0 },
    speed: 0.75, los: 8, pop: 1, age: 2, trainedAt: 'monastery', radius: 0.22, model: 'monk', weapon: 'staff',
    description: 'Heals every friendly unit within one tile, as fast as that unit was trained. Seeks out the wounded nearby when idle.',
  },
  {
    key: 'engineer', name: 'Engineer', classes: C.Engineer, cost: [100, 0, 75, 0], trainTime: 40,
    hp: 40, armor: { melee: 1, pierce: 1 },
    attack: { melee: 3, pierce: 0, range: 0, cooldown: 2 },
    speed: 0.9, los: 5, pop: 1, age: 3, trainedAt: 'university', radius: 0.22, model: 'engineer', weapon: 'hammer', helmet: 'cap',
    description: 'Commandeers enemy buildings once no defenders are within 8 tiles: right-click one. Takes half the building\'s build time. Also repairs your own buildings three times as fast as a villager.',
  },
  {
    key: 'caravelle', name: 'Caravelle', classes: C.Ship, cost: [0, 125, 0, 0], trainTime: 40,
    hp: 180, armor: { melee: 0, pierce: 6 },
    speed: 1.45, los: 6, pop: 1, age: 1, trainedAt: 'dock', radius: 0.7, model: 'ship', weapon: 'none', garrison: 20,
    description: 'Transport ship. Carries 20 units across water; mounted units and siege take two places. Right-click it with land units to board, then right-click the shore to land them.',
  },
];
UNITS.push(...LATER_UNITS);
